import React, { useState, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Checkbox, FormControlLabel,
  Chip, CircularProgress, Alert, Divider, IconButton,
  Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Paper, ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import DialogHeader from '../Common/DialogHeader';
import { useTheme, alpha } from '@mui/material/styles';
import weddingEventService from '../../services/weddingEventService';

const RSVP_LABEL = { NO_RESPONSE: 'Nincs válasz', ATTENDING: 'Részt vesz', NOT_ATTENDING: 'Nem jön', MAYBE: 'Talán' };
const RSVP_COLOR = { NO_RESPONSE: 'default', ATTENDING: 'success', NOT_ATTENDING: 'error', MAYBE: 'warning' };

export default function SendInvitationsDialog({ open, onClose, event, guests, onSuccess }) {
  const theme = useTheme();
  const [step, setStep] = useState(0); // 0=kiválasztás, 1=összesítő, 2=küldés/eredmény
  const [selected, setSelected] = useState(new Set());
  const [filter, setFilter] = useState('all'); // 'all' | 'no_email' | 'not_sent' | 'no_response'
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);

  const eligibleGuests = useMemo(() => guests.filter(g => g.email), [guests]);
  const noEmailCount = guests.length - eligibleGuests.length;

  const filteredGuests = useMemo(() => {
    switch (filter) {
      case 'not_sent': return eligibleGuests.filter(g => !g.invitationSentAt);
      case 'no_response': return eligibleGuests.filter(g => g.rsvpStatus === 'NO_RESPONSE');
      default: return eligibleGuests;
    }
  }, [eligibleGuests, filter]);

  const handleToggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleSelectAll = (checked) => {
    if (checked) setSelected(new Set(filteredGuests.map(g => g.id)));
    else setSelected(new Set());
  };

  const allChecked = filteredGuests.length > 0 && filteredGuests.every(g => selected.has(g.id));
  const someChecked = filteredGuests.some(g => selected.has(g.id)) && !allChecked;

  const selectedGuests = guests.filter(g => selected.has(g.id));
  const alreadySentCount = selectedGuests.filter(g => g.invitationSentAt).length;

  const handleSend = async () => {
    setSending(true);
    setStep(2);
    try {
      const res = await weddingEventService.sendInvitations(event.id, [...selected]);
      setResults(res.data || []);
    } catch (err) {
      setResults([{ status: 'failed', reason: err.message }]);
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    if (sending) return;
    if (results) onSuccess?.();
    else onClose();
    // Reset
    setTimeout(() => {
      setStep(0);
      setSelected(new Set());
      setFilter('all');
      setResults(null);
    }, 300);
  };

  const sentCount = results?.filter(r => r.status === 'sent').length || 0;
  const failedCount = results?.filter(r => r.status === 'failed').length || 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ p: 0 }}>
        <DialogHeader
          icon={<SendIcon />}
          title={
            step === 2
              ? (sending ? 'Küldés folyamatban...' : 'Küldés kész')
              : step === 0 ? 'Vendégek kiválasztása' : 'Küldési összesítő'
          }
          subtitle={step < 2 ? `${step + 1} / 2. lépés` : undefined}
          onClose={handleClose}
        />
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* STEP 0: Kiválasztás */}
        {step === 0 && (
          <Box sx={{ p: 3 }}>
            {noEmailCount > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {noEmailCount} vendégnek nincs megadva email cím — ők nem választhatók.
              </Alert>
            )}

            {/* Szűrő */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Szűrés:
              </Typography>
              <ToggleButtonGroup
                value={filter}
                exclusive
                onChange={(_, v) => v && setFilter(v)}
                size="small"
              >
                <ToggleButton value="all">Mindenki ({eligibleGuests.length})</ToggleButton>
                <ToggleButton value="not_sent">Még nem kapott ({eligibleGuests.filter(g => !g.invitationSentAt).length})</ToggleButton>
                <ToggleButton value="no_response">Nincs visszajelzés ({eligibleGuests.filter(g => g.rsvpStatus === 'NO_RESPONSE').length})</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Összes kijelölése */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, px: 1 }}>
              <Checkbox
                checked={allChecked}
                indeterminate={someChecked}
                onChange={e => handleSelectAll(e.target.checked)}
              />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Összes kijelölése ({filteredGuests.length} fő)
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Chip label={`${selected.size} kijelölve`} size="small" color="primary" />
            </Box>

            <Divider sx={{ mb: 1 }} />

            {/* Lista */}
            <TableContainer sx={{ maxHeight: 380, overflowY: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell sx={{ fontWeight: 700 }}>Név</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>RSVP</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Meghívó kiküldve</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredGuests.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="text.secondary">Nincs vendég ebben a szűrőben</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {filteredGuests.map(g => (
                    <TableRow
                      key={g.id}
                      hover
                      onClick={() => handleToggle(g.id)}
                      sx={{ cursor: 'pointer', bgcolor: selected.has(g.id) ? alpha(theme.palette.primary.light, 0.12) : 'inherit' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox checked={selected.has(g.id)} onChange={() => handleToggle(g.id)} onClick={e => e.stopPropagation()} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{g.lastName} {g.firstName}</Typography>
                      </TableCell>
                      <TableCell><Typography variant="body2">{g.email}</Typography></TableCell>
                      <TableCell>
                        <Chip label={RSVP_LABEL[g.rsvpStatus]} color={RSVP_COLOR[g.rsvpStatus]} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        {g.invitationSentAt
                          ? <Chip icon={<CheckIcon />} label="Igen" size="small" color="success" variant="outlined" />
                          : <Chip label="Nem" size="small" variant="outlined" />
                        }
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* STEP 1: Összesítő */}
        {step === 1 && (
          <Box sx={{ p: 3 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              A következő vendégeknek küldjük ki a meghívót. Ellenőrizd az összesítőt!
            </Alert>

            {alreadySentCount > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {alreadySentCount} vendég már kapott meghívót — nekik újraküldésre kerül.
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                  {selected.size}
                </Typography>
                <Typography variant="caption" color="text.secondary">Összes küldés</Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" sx={{ fontWeight: 700, color: 'warning.main' }}>
                  {alreadySentCount}
                </Typography>
                <Typography variant="caption" color="text.secondary">Újraküldés</Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" sx={{ fontWeight: 700, color: 'success.main' }}>
                  {selected.size - alreadySentCount}
                </Typography>
                <Typography variant="caption" color="text.secondary">Első küldés</Typography>
              </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <TableContainer sx={{ maxHeight: 300, overflowY: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.secondary.light, 0.15) }}>
                    <TableCell sx={{ fontWeight: 700 }}>Név</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Megjegyzés</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedGuests.map(g => (
                    <TableRow key={g.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{g.lastName} {g.firstName}</Typography>
                      </TableCell>
                      <TableCell><Typography variant="body2">{g.email}</Typography></TableCell>
                      <TableCell>
                        {g.invitationSentAt && (
                          <Chip label="Újraküldés" size="small" color="warning" variant="outlined" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* STEP 2: Eredmény */}
        {step === 2 && (
          <Box sx={{ p: 3 }}>
            {sending ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress size={48} color="primary" />
                <Typography sx={{ mt: 2 }}>Meghívók kiküldése folyamatban...</Typography>
              </Box>
            ) : (
              <>
                {failedCount === 0 ? (
                  <Alert severity="success" icon={<CheckIcon />} sx={{ mb: 2 }}>
                    Minden meghívó sikeresen el lett küldve!
                  </Alert>
                ) : (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {sentCount} sikeresen kiküldve, {failedCount} sikertelen.
                  </Alert>
                )}
                <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>{sentCount}</Typography>
                    <Typography variant="caption" color="text.secondary">Sikeres</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'error.main' }}>{failedCount}</Typography>
                    <Typography variant="caption" color="text.secondary">Sikertelen</Typography>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2.5, gap: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
        {step === 0 && (
          <>
            <Button onClick={handleClose} color="inherit">Mégse</Button>
            <Button
              variant="contained"
              color="primary"
              endIcon={<NextIcon />}
              disabled={selected.size === 0}
              onClick={() => setStep(1)}
            >
              Összesítő megtekintése ({selected.size})
            </Button>
          </>
        )}
        {step === 1 && (
          <>
            <Button startIcon={<BackIcon />} onClick={() => setStep(0)} color="inherit">Vissza</Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SendIcon />}
              onClick={handleSend}
            >
              Küldés megerősítése
            </Button>
          </>
        )}
        {step === 2 && !sending && (
          <Button variant="contained" color="primary" onClick={handleClose}>
            Bezárás
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
